import React, { useCallback, useEffect, useState, useMemo } from 'react';
import propTypes from 'prop-types';
import SimpleTable, { createDefaultActions } from '../../../Common/SimpleTable/SimpleTable';
import _ from 'lodash';
import ContentDialog from './ContentDialog';
import useBoolean from '../../../../utils/hooks/useBoolean';
import { RecursiveShapeType } from '../../../../utils/recursiveShapeType';


const ContentBindingTable = ({ title, columns, data, dialogModel, updatedContent, onSave, globalActions }) => {
  const [isOpen, showDialog, hideDialog] = useBoolean(false);
  const [editContent, setEditContent] = useState({});
  const [tableData, setTableData] = useState(data);
  const [editRow, setEditRow] = useState(null);

  const addContent = useCallback(() => {
    setEditContent({});
    showDialog();
  }, [showDialog]);

  const onCancel = useCallback(() => {
    hideDialog();
  }, [hideDialog]);

  const _onSave = useCallback((item) => {
    let data = [...tableData];
    const match = _.find(data, (row) => row.contentId === editRow?.contentId);
    if (match) {
      const i = _.indexOf(data, _.find(data, (row) => row.contentId === editRow?.contentId));
      data.splice(i, 1, item);
      setTableData(data);
      onSave(data);
    } else {
      setTableData([...data, item]);
      onSave([...data, item]);
    }
    hideDialog();
  }, [editRow?.contentId, hideDialog, onSave, tableData]);

  const _onRowClick = useCallback((item) => {
    setEditRow(item);
    setEditContent(item);
    showDialog();
  }, [showDialog]);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const onDelete = useCallback((item) => {
    _.reject(tableData, item);
    onSave(_.reject(tableData, item));
  }, [onSave, tableData]);

  const actions = useMemo(() => [
    ...createDefaultActions({ onDelete, onEdit: _onRowClick })
  ], [onDelete, _onRowClick]);

  return (
    <>
      <SimpleTable
        actions={actions}
        columns={columns}
        data={tableData}
        onRowClick={_onRowClick}
        globalActions={globalActions ?? [{ onClick: addContent }]}
      />
      <ContentDialog
        title={title}
        model={dialogModel}
        isOpen={isOpen}
        onSave={_onSave}
        onCancel={onCancel}
        editContent={editContent}
        updatedContent={updatedContent}
      />
    </>
  );
};

ContentBindingTable.propTypes = {
  title: propTypes.string.isRequired,
  columns: propTypes.arrayOf(RecursiveShapeType({
    label: propTypes.string.isRequired,
    path: propTypes.string.isRequired,
    searchItem: propTypes.bool,
    isSortable: propTypes.bool
  })),
  data: propTypes.arrayOf(Object),
  dialogModel: propTypes.arrayOf(RecursiveShapeType({
    type: propTypes.string.isRequired,
    label: propTypes.string.isRequired,
    path: propTypes.string.isRequired,
    isRequired: propTypes.bool,
    hasError: propTypes.func,
    options: propTypes.arrayOf(RecursiveShapeType({
      text: propTypes.string.isRequired,
      value: propTypes.string.isRequired
    })),
    properties: propTypes.object
  })),
  updatedContent: propTypes.func,
  onSave: propTypes.func
};

export default ContentBindingTable;
